import { action } from '@ember/object';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import type EmployeeModel from 'garaje/models/employee';
import type GenericResourceModel from 'garaje/models/generic-resource';

interface UnplacedResourcesPanelArgs {
  unplacedParking: GenericResourceModel[];
  selectedUnplacedResource: GenericResourceModel | null;
  onSelectUnplacedResource: (resource: GenericResourceModel | null) => void;
  closeUnplacedParkingPanel: () => void;
}

interface ResourceWithEmployee {
  id: string;
  name: string;
  employeeName: string | null;
  resource: GenericResourceModel;
}

export default class UnplacedResourcesPanel extends Component<UnplacedResourcesPanelArgs> {
  @service declare store: StoreService;

  @tracked searchTerm = '';

  constructor(owner: unknown, args: UnplacedResourcesPanelArgs) {
    super(owner, args);
    // Load employees once when component is initialized
    void this.loadEmployeesWithUnplacedParkingTask.perform();
  }

  // Keep selectedResourceId in sync with the parent's selectedUnplacedResource
  get selectedResourceId(): string | null {
    return this.args.selectedUnplacedResource?.id || null;
  }

  get employeesWithUnplacedParking(): EmployeeModel[] {
    return this.loadEmployeesWithUnplacedParkingTask.lastSuccessful?.value || [];
  }

  get resourcesWithEmployees(): ResourceWithEmployee[] {
    const { unplacedParking } = this.args;
    const employees = this.employeesWithUnplacedParking;

    return unplacedParking.map((resource) => {
      const assignment = resource.assignments?.[0];
      const employee = assignment?.employeeId
        ? employees.find((emp) => emp.id === assignment.employeeId.toString())
        : null;

      return {
        id: resource.id,
        name: resource.name,
        employeeName: employee?.name || null,
        resource: resource,
      };
    });
  }

  get filteredResourcesWithEmployees(): ResourceWithEmployee[] {
    const { searchTerm } = this;
    const resources = this.resourcesWithEmployees;

    if (!searchTerm.trim()) {
      return resources;
    }

    const searchLower = searchTerm.toLowerCase();
    return resources.filter((item) => {
      const nameMatches = item.name.toLowerCase().includes(searchLower);
      const employeeMatches = item.employeeName?.toLowerCase().includes(searchLower) || false;
      return nameMatches || employeeMatches;
    });
  }

  @action
  getEmployeeForResource(resource: GenericResourceModel): EmployeeModel | null {
    const assignment = resource.assignments?.[0];
    if (!assignment?.employeeId) {
      return null;
    }

    return (
      this.employeesWithUnplacedParking.find((employee) => employee.id === assignment.employeeId.toString()) || null
    );
  }

  loadEmployeesWithUnplacedParkingTask = task({ drop: true }, async () => {
    const { unplacedParking } = this.args;

    // Extract all employee IDs from assignments
    const employeeIds = unplacedParking
      .flatMap((resource) => resource.assignments || [])
      .map((assignment) => assignment.employeeId)
      .filter(Boolean);

    // Get unique employee IDs
    const uniqueEmployeeIds = [...new Set(employeeIds)];

    if (uniqueEmployeeIds.length === 0) {
      return [];
    }

    // Check which employees are already loaded in the store
    const employeesFromStore: EmployeeModel[] = [];
    const missingEmployeeIds: string[] = [];

    uniqueEmployeeIds.forEach((employeeId) => {
      const employee = this.store.peekRecord('employee', employeeId.toString());
      if (employee) {
        employeesFromStore.push(employee);
      } else {
        missingEmployeeIds.push(employeeId.toString());
      }
    });

    // Query missing employees from the store
    let fetchedEmployees: EmployeeModel[] = [];
    if (missingEmployeeIds.length > 0) {
      const employeesData = await this.store.query('employee', {
        filter: {
          id: missingEmployeeIds.join(','),
        },
        fields: {
          employees: 'name',
        },
      });
      fetchedEmployees = employeesData.slice();
    }

    // Return the combined employees instead of setting a tracked property
    return [...employeesFromStore, ...fetchedEmployees];
  });

  get showClearButton(): boolean {
    return this.searchTerm.length > 0;
  }

  @action
  handleSelectUnplacedResource(unplacedResource: GenericResourceModel): void {
    // If clicking on the already selected resource, deselect it
    if (this.selectedResourceId === unplacedResource.id) {
      this.args.onSelectUnplacedResource(null);
    } else {
      // Otherwise, select the new resource
      this.args.onSelectUnplacedResource(unplacedResource);
    }
  }

  @action
  handleSearchResources(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm = target.value;
  }

  @action
  handleClearSearch(): void {
    this.searchTerm = '';
  }
}
